import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import Moment from 'moment/moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { formatToText } from '../../../../common-utils/formatters';

type TableRow = Record<string, unknown>;

interface TableProps {
  data: TableRow[];
  headers: string[];
  tableClass?: string;
  columnChecking?: boolean;
  idKey?: string;
  dataChangeCallback?: (checked: boolean, id: string) => void;
  noPaddingColumns?: string[];
  autoresize?: boolean;
  tbodyClass?: string;
  isSearchable?: boolean;
  sortableColumns?: Record<string, string>;
  setParentFilteredData?: (data: TableRow[]) => void;
  validColumnLabel?: string;
  mergedCellsConfig?: (item: TableRow, index?: number) => { [header: string]: { colSpan: number; content: string | JSX.Element } };
  stickyHeader?: boolean;
}

const Table = ({
  data,
  headers,
  tableClass = '',
  columnChecking = false,
  idKey = 'id',
  dataChangeCallback = null,
  noPaddingColumns = null,
  autoresize = true,
  tbodyClass = '',
  isSearchable = false,
  sortableColumns = null,
  setParentFilteredData = null,
  validColumnLabel = 'VALIDE',
  mergedCellsConfig = () => ({}),
  stickyHeader = false,
}: TableProps) => {
  const autoResizeClass = autoresize ? ' table-auto' : ' table-fixed';

  const [sortConfig, setSortConfig] = useState({ 'key': '', 'direction': '', 'type': '' });
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchInputChange = (text: string) => {
    setSearchQuery(text);
  };

  const handleCheckboxChange = (checked: boolean, id: string) => {
    dataChangeCallback(checked, id);
  };

  const requestSort = (key: string, type: string) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction, type });
  };

  const sortedAndFilteredData = useMemo(() => {
    let filteredData = data;

    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    if (normalizedSearchQuery) {
      filteredData = filteredData.filter((row) => {
        return headers.some((header) => {
          const cellText = formatToText(row[header] as React.ReactNode).toLowerCase();
          return cellText.includes(normalizedSearchQuery);
        });
      });
    }

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        const rawKey = `${sortConfig.key}__raw`;
        const useRaw = Object.prototype.hasOwnProperty.call(a, rawKey) && Object.prototype.hasOwnProperty.call(b, rawKey);
        let firstValue = useRaw ? a[rawKey] : a[sortConfig.key];
        let secondValue = useRaw ? b[rawKey] : b[sortConfig.key];

        firstValue = formatToText(firstValue as React.ReactNode);
        secondValue = formatToText(secondValue as React.ReactNode);

        if (sortConfig.type === 'date') {
          firstValue = Moment(firstValue, 'DD/MM/YYYY').toDate();
          secondValue = Moment(secondValue, 'DD/MM/YYYY').toDate();
        }

        if (sortConfig.type === 'number') {
          if (typeof firstValue === 'string') {
            firstValue = parseFloat(firstValue.replaceAll(/\s+|€/g, '').replaceAll(',', '.'));
          } else if (typeof firstValue !== 'number') {
            firstValue = parseFloat(String(firstValue).replaceAll(/\s+|€/g, '').replaceAll(',', '.'));
          }

          if (typeof secondValue === 'string') {
            secondValue = parseFloat(secondValue.replaceAll(/\s+|€/g, '').replaceAll(',', '.'));
          } else if (typeof secondValue !== 'number') {
            secondValue = parseFloat(String(secondValue).replaceAll(/\s+|€/g, '').replaceAll(',', '.'));
          }
        }

        if (firstValue < secondValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (firstValue > secondValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }

        return 0;
      });
    }

    return filteredData;
  }, [data, headers, searchQuery, sortConfig]);

  useEffect(() => {
    if (setParentFilteredData && searchQuery.length > 0) {
      setParentFilteredData(sortedAndFilteredData);
    }
  }, [searchQuery, sortedAndFilteredData, setParentFilteredData]);

  const mergedCellsConfigMap = useMemo(() => {
    const mergedCells: { [key: string]: { colSpan: number; content: string | JSX.Element } } = {};
    if (Array.isArray(data)) {
      data.forEach((item, rowIndex) => {
        const config = mergedCellsConfig(item, rowIndex);
        headers.forEach((header) => {
          if (config[header]) {
            mergedCells[`${rowIndex}-${header}`] = config[header];
          }
        });
      });
    }

    return mergedCells;
  }, [data, headers, mergedCellsConfig]);

  const getCellKey = (rowIndex: number, header: string) => `${rowIndex}-${header}`;

  const theadRef = useRef<HTMLTableSectionElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (!stickyHeader) {return;}

    const handleScroll = () => {
      if (!tableRef.current || !theadRef.current) {return;}

      const tableRect = tableRef.current.getBoundingClientRect();
      const theadRect = theadRef.current.getBoundingClientRect();
      
      // Calculate offset dynamically based on the main app header height
      // We look for the main sticky nav or fallback to reasonable defaults
      const mainHeader = document.querySelector('nav.sticky');
      const STICKY_OFFSET = mainHeader ? mainHeader.getBoundingClientRect().height : (window.innerWidth >= 1024 ? 150 : 76);

      let translateY = 0;
      // If top of table is above the sticky offset (scrolled past)
      if (tableRect.top < STICKY_OFFSET) {
        translateY = STICKY_OFFSET - tableRect.top;
        
        // Don't scroll past the bottom of the table
        const maxTranslate = tableRect.height - theadRect.height;
        translateY = Math.min(translateY, maxTranslate);
      }

      const ths = theadRef.current.querySelectorAll('th');
      ths.forEach((th) => {
        th.style.transform = `translateY(${translateY}px)`;
        // Add z-index and position relative to ensure it sits on top of body content
        if (translateY > 0) {
          th.style.zIndex = '20';
          th.style.position = 'relative'; 
        } else {
          th.style.zIndex = '';
          th.style.position = '';
        }
      });
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    
    // Initial calculation
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
      
      // Cleanup
      if (theadRef.current) {
        const ths = theadRef.current.querySelectorAll('th');
        ths.forEach((th) => {
          th.style.transform = '';
          th.style.zIndex = '';
          th.style.position = '';
        });
      }
    };
  }, [stickyHeader]);

  // Sort indicator component for cleaner rendering
  const SortIndicator = ({ header }: { header: string }) => {
    const isActive = sortConfig.key === header;
    const isAscending = sortConfig.direction === 'ascending';
    
    if (!isActive) {
      return (
        <span className="ml-1.5 inline-flex flex-col opacity-40 transition-opacity group-hover:opacity-70">
          <ChevronUpIcon className="-mb-1 h-3 w-3" />
          <ChevronDownIcon className="h-3 w-3" />
        </span>
      );
    }
    
    return (
      <span className="ml-1.5 inline-flex text-white">
        {isAscending ? (
          <ChevronUpIcon className="h-4 w-4" strokeWidth={2.5} />
        ) : (
          <ChevronDownIcon className="h-4 w-4" strokeWidth={2.5} />
        )}
      </span>
    );
  };

  // Custom checkbox component for better styling
  const CustomCheckbox = ({ checked, onChange }: { checked: boolean; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
    <label className="group relative inline-flex cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="peer sr-only"
      />
      <div className={`
        flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all duration-200
        ${checked 
          ? 'border-rgpd-blue bg-rgpd-blue shadow-sm' 
          : 'border-gray-300 bg-white group-hover:border-rgpd-blue-light'}
      `}>
        {checked && <CheckIcon className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
      </div>
    </label>
  );

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      {isSearchable && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-md flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="
                block w-full rounded-xl border border-gray-200
                bg-white py-2.5 pr-4 pl-10
                text-sm text-gray-900 placeholder-gray-400
                shadow-sm
                transition-all duration-200
                hover:border-gray-300 focus:border-rgpd-blue focus:ring-2 focus:ring-rgpd-blue/20
                focus:outline-none
              "
              placeholder="Rechercher dans le tableau..."
              onChange={(e) => handleSearchInputChange(e.target.value)}
              value={searchQuery}
              autoComplete="off"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-400 transition-colors hover:text-gray-600"
              >
                <XMarkIcon className="h-4 w-4" strokeWidth={2} />
              </button>
            )}
          </div>
          {searchQuery && (
            <span className="text-sm text-gray-500">
              {sortedAndFilteredData.length} résultat{sortedAndFilteredData.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* Table Container */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table ref={tableRef} className={`w-full divide-y divide-gray-200 ${autoResizeClass} ${tableClass}`}>
            {/* Table Header */}
            <thead ref={theadRef}>
              <tr className={stickyHeader ? '' : 'bg-rgpd-blue'}>
                {columnChecking && (
                  <th 
                    className={`
                      px-4 py-3.5 text-left text-xs font-semibold tracking-wider text-white uppercase
                      ${stickyHeader ? 'bg-rgpd-blue' : ''}
                    `}
                  >
                    <span className="flex items-center justify-center">{validColumnLabel}</span>
                  </th>
                )}
                {headers.map((header, index) => {
                  const isSortable = sortableColumns !== null && sortableColumns[header] !== undefined;
                  
                  return (
                    <th
                      className={`
                        px-4 py-3.5 text-left text-xs font-semibold tracking-wider text-white uppercase
                        ${stickyHeader ? 'bg-rgpd-blue' : ''}
                        ${isSortable ? 'group cursor-pointer select-none' : ''}
                        transition-colors duration-150
                      `}
                      key={index}
                      onClick={isSortable ? () => requestSort(header, sortableColumns[header]) : undefined}
                    >
                      <div className="flex items-center">
                        <span className={isSortable ? 'group-hover:text-white/90' : ''}>
                          {header}
                        </span>
                        {isSortable && <SortIndicator header={header} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className={`divide-y divide-gray-100 bg-white ${tbodyClass}`}>
              {sortedAndFilteredData.map((item, rowIndex) => {
                  const renderedCells: Set<string> = new Set();

                  return (
                    <tr 
                      key={String(item[idKey])}
                      className="transition-colors duration-150 hover:bg-gray-50/80"
                    >
                      {columnChecking && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex justify-center">
                            <CustomCheckbox
                              checked={Boolean(item.VALIDE)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e.target.checked, String(item[idKey]))}
                            />
                          </div>
                        </td>
                      )}
                      {headers.map((header, colIndex) => {
                        const cellKey = getCellKey(rowIndex, header);
                        const mergedCell = mergedCellsConfigMap[cellKey];
                        const isMerged = mergedCell !== undefined;

                        // Determine if the cell should be rendered or not
                        const shouldRenderCell = !renderedCells.has(cellKey);

                        // Update the renderedCells set
                        if (isMerged && shouldRenderCell) {
                          const spanCount = mergedCell.colSpan || 1;
                          for (let i = 1; i < spanCount; i++) {
                            renderedCells.add(getCellKey(rowIndex, headers[headers.indexOf(header) + i]));
                          }
                        }

                        return (
                          shouldRenderCell ? (
                            <td
                              className={`
                                ${noPaddingColumns && noPaddingColumns.includes(header) ? 'p-0' : 'px-4 py-3'}
                                text-left text-sm text-gray-700
                                ${colIndex === 0 ? 'font-medium text-gray-900' : ''}
                              `}
                              colSpan={isMerged ? mergedCell.colSpan : undefined}
                              key={colIndex}
                            >
                              {isMerged ? mergedCell.content : (item[header] as React.ReactNode)}
                            </td>
                          ) : null
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Table;
